// src/components/BurnLogger.js
import React, { useState } from 'react';

export default function BurnLogger({ kiln, existingBurn, onClose, onSuccess }) {

  // Work out which step to start on based on existing burn
  const getStartStep = () => {
    if (!existingBurn) return 1;
    const steps = JSON.parse(existingBurn.steps_completed || '[]');
    if (!steps.includes('step1')) return 1;
    if (!steps.includes('step2')) return 2;
    if (!steps.includes('step3')) return 3;
    if (!steps.includes('step4')) return 4;
    return 1;
  };

  const [currentStep, setCurrentStep] = useState(getStartStep());
  const [burnId, setBurnId] = useState(existingBurn?.burn_id || null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    // Step 1: Biomass — pre-fill from existing burn if continuing
    biomass_type: existingBurn?.biomass_type || '',
    biomass_quantity: existingBurn?.biomass_quantity || '',
    biomass_unit: existingBurn?.biomass_unit || 'buckets',
    // Step 2: Burn
    burn_date: existingBurn?.burn_date || new Date().toISOString().split('T')[0],
    burn_photo: null,
    burn_photo_preview: null,
    burn_photo_url: existingBurn?.burn_photo_url || null,
    // Step 3: Activation
    activation_method: existingBurn?.activation_method || 'urine_water',
    activation_start_date: existingBurn?.activation_start_date || '',
    // Step 4: Soil
    application_date: existingBurn?.application_date || '',
    application_area: existingBurn?.application_area || '',
    application_photo: null,
    application_photo_preview: null,
    application_photo_url: existingBurn?.application_photo_url || null,
    notes: existingBurn?.notes || ''
  });

  const API_BASE = process.env.REACT_APP_API_URL || '';

  const biomassTypes = [
    'Maize stalks',
    'Sorghum residue',
    'Cassava residue',
    'Sugarcane bagasse',
    'Banana leaves/stems',
    'Fruit tree prunings',
    'Wood scraps',
    'Household organic waste',
    'Mixed biomass'
  ];

  // Photo upload function
  const uploadPhoto = async (file, step) => {
    if (!file || !burnId) return null;
    
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('photo', file);
      
      const res = await fetch(`${API_BASE}/api/photos/burn/${burnId}/${step}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataUpload
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to upload photo');
      }

      const data = await res.json();
      console.log(`📸 Photo uploaded for step ${step}:`, data.photo_url);
      return data.photo_url;
    } catch (err) {
      console.error('Photo upload error:', err);
      setError(`Photo upload failed: ${err.message}`);
      return null;
    } finally {
      setUploading(false);
    }
  };

  // Handle file selection
  const handlePhotoSelect = (e, photoType) => {
    const file = e.target.files[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          [photoType]: file,
          [`${photoType}_preview`]: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleStep1Submit = async () => {
    if (!formData.biomass_type || !formData.biomass_quantity) {
      setError('Please fill in biomass type and quantity');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');

      // If continuing existing burn, just update and move to next step
      if (burnId) {
        const res = await fetch(`${API_BASE}/api/burns/${burnId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            biomass_type: formData.biomass_type,
            biomass_quantity: formData.biomass_quantity,
            biomass_unit: formData.biomass_unit,
            steps_completed: ['step1']
          })
        });
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to update burn log');
        }
        setCurrentStep(2);
        return;
      }

      // New burn — POST
      const res = await fetch(`${API_BASE}/api/burns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          kiln_id: kiln.kiln_id,
          farmer_id: kiln.farmer_id,
          burn_date: formData.burn_date,
          biomass_type: formData.biomass_type,
          biomass_quantity: formData.biomass_quantity,
          biomass_unit: formData.biomass_unit
        })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to create burn log');
      }

      const data = await res.json();
      setBurnId(data.burn_id);
      setCurrentStep(2);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateBurn = async (updates, nextStep) => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/burns/${burnId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(updates)
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update burn log');
      }

      if (nextStep) {
        setCurrentStep(nextStep);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStep2Submit = async () => {
    setLoading(true);
    
    // Upload photo if selected
    if (formData.burn_photo) {
      const photoUrl = await uploadPhoto(formData.burn_photo, 2);
      if (photoUrl) {
        setFormData(prev => ({ ...prev, burn_photo_url: photoUrl }));
      }
    }
    
    await updateBurn({
      burn_start_time: new Date().toISOString(),
      steps_completed: ['step1', 'step2']
    }, 3);
  };

  const handleStep3Submit = () => {
    if (!formData.activation_start_date) {
      setError('Please enter activation start date');
      return;
    }

    updateBurn({
      activation_method: formData.activation_method,
      activation_start_date: formData.activation_start_date,
      steps_completed: ['step1', 'step2', 'step3']
    }, 4);
  };

  const handleStep4Submit = async () => {
    if (!formData.application_date) {
      setError('Please enter application date');
      return;
    }

    setLoading(true);

    // Upload photo if selected
    if (formData.application_photo) {
      const photoUrl = await uploadPhoto(formData.application_photo, 4);
      if (photoUrl) {
        setFormData(prev => ({ ...prev, application_photo_url: photoUrl }));
      }
    }

    await updateBurn({
      applied_to_soil: 1,
      application_date: formData.application_date,
      application_area: formData.application_area,
      steps_completed: ['step1', 'step2', 'step3', 'step4'],
      status: 'complete'
    });

    // Complete the burn
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_BASE}/api/burns/${burnId}/complete`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      alert('🎉 Burn completed! Credits have been calculated.');
      onSuccess();
    } catch (err) {
      setError(err.message);
    }
  };

  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modal: {
      background: '#1a3327',
      padding: '24px',
      borderRadius: '12px',
      maxWidth: '500px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      border: '2px solid #2d5a45'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    title: {
      margin: 0,
      color: 'white',
      fontSize: '20px'
    },
    kilnBadge: {
      background: '#0f2419',
      padding: '6px 12px',
      borderRadius: '6px',
      color: '#4ade80',
      fontSize: '12px',
      fontFamily: 'monospace'
    },
    stepIndicator: {
      display: 'flex',
      justifyContent: 'center',
      gap: '20px',
      marginBottom: '24px'
    },
    stepCircle: (active, completed) => ({
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      background: completed ? '#4ade80' : active ? '#4ade80' : '#2d5a45',
      color: completed || active ? '#0f2419' : '#888',
      fontSize: '14px'
    }),
    stepLabel: {
      textAlign: 'center',
      color: '#888',
      fontSize: '12px',
      marginTop: '6px'
    },
    stepContent: {
      background: '#0f2419',
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '20px'
    },
    stepTitle: {
      color: '#4ade80',
      margin: '0 0 16px 0',
      fontSize: '18px'
    },
    infoBox: {
      background: '#1a3327',
      padding: '12px',
      borderRadius: '6px',
      marginBottom: '16px',
      color: '#888',
      fontSize: '13px',
      lineHeight: '1.5'
    },
    formGroup: {
      marginBottom: '16px'
    },
    label: {
      display: 'block',
      color: '#888',
      marginBottom: '6px',
      fontSize: '12px',
      textTransform: 'uppercase'
    },
    input: {
      width: '100%',
      padding: '12px',
      background: '#1a3327',
      border: '1px solid #2d5a45',
      borderRadius: '6px',
      color: 'white',
      fontSize: '14px',
      boxSizing: 'border-box'
    },
    select: {
      width: '100%',
      padding: '12px',
      background: '#1a3327',
      border: '1px solid #2d5a45',
      borderRadius: '6px',
      color: 'white',
      fontSize: '14px',
      boxSizing: 'border-box'
    },
    photoUpload: {
      border: '2px dashed #2d5a45',
      borderRadius: '8px',
      padding: '20px',
      textAlign: 'center',
      cursor: 'pointer',
      transition: 'border-color 0.3s'
    },
    photoPreview: {
      maxWidth: '100%',
      maxHeight: '150px',
      borderRadius: '8px',
      marginTop: '10px'
    },
    uploadIcon: {
      fontSize: '32px',
      marginBottom: '8px'
    },
    buttonRow: {
      display: 'flex',
      gap: '10px',
      marginTop: '20px'
    },
    btnPrimary: {
      flex: 1,
      padding: '14px 20px',
      background: '#4ade80',
      color: '#0f2419',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: 'bold',
      fontSize: '14px'
    },
    btnSecondary: {
      flex: 1,
      padding: '14px 20px',
      background: '#f59e0b',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: 'bold',
      fontSize: '14px'
    },
    btnBack: {
      flex: 1,
      padding: '14px 20px',
      background: '#2d5a45',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontWeight: 'bold',
      fontSize: '14px'
    },
    error: {
      background: '#7f1d1d',
      color: '#fecaca',
      padding: '12px',
      borderRadius: '6px',
      marginBottom: '16px',
      fontSize: '13px'
    },
    safetyBox: {
      background: '#422006',
      border: '1px solid #f59e0b',
      padding: '16px',
      borderRadius: '8px',
      marginTop: '16px'
    },
    safetyTitle: {
      color: '#f59e0b',
      fontWeight: 'bold',
      marginBottom: '8px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    safetyList: {
      color: '#fef3c7',
      fontSize: '13px',
      margin: 0,
      paddingLeft: '20px'
    },
    readyBox: {
      background: '#14532d',
      padding: '16px',
      borderRadius: '8px',
      marginTop: '16px'
    },
    readyTitle: {
      color: '#4ade80',
      fontWeight: 'bold',
      marginBottom: '4px'
    },
    readyText: {
      color: '#86efac',
      fontSize: '13px',
      margin: 0
    },
    celebrationBox: {
      background: 'linear-gradient(135deg, #14532d 0%, #1a3327 100%)',
      padding: '20px',
      borderRadius: '8px',
      textAlign: 'center',
      marginTop: '16px',
      border: '2px solid #4ade80'
    },
    celebrationTitle: {
      color: '#4ade80',
      fontSize: '18px',
      fontWeight: 'bold',
      marginBottom: '8px'
    },
    celebrationText: {
      color: '#86efac',
      fontSize: '13px'
    },
    uploadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '8px',
      color: '#4ade80'
    }
  };

  const steps = [
    { num: 1, label: 'Biomass' },
    { num: 2, label: 'Burn' },
    { num: 3, label: 'Activate' },
    { num: 4, label: 'Soil' }
  ];

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h2 style={styles.title}>{existingBurn ? '▶ Continue Burn' : 'Log Burn'}</h2>
          <span style={styles.kilnBadge}>{kiln.kiln_id}</span>
        </div>
        {existingBurn && (
          <div style={{ background: '#0f3320', border: '1px solid #4ade80', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px', color: '#4ade80' }}>
            Continuing burn <strong>{existingBurn.burn_id}</strong> — pick up from where you left off
          </div>
        )}

        {/* Step Indicator */}
        <div style={styles.stepIndicator}>
          {steps.map(step => (
            <div key={step.num}>
              <div style={styles.stepCircle(currentStep === step.num, currentStep > step.num)}>
                {currentStep > step.num ? '✓' : step.num}
              </div>
              <div style={styles.stepLabel}>{step.label}</div>
            </div>
          ))}
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {/* Step 1: Biomass */}
        {currentStep === 1 && (
          <div style={styles.stepContent}>
            <h3 style={styles.stepTitle}>Step 1: Approved Biomass</h3>
            <div style={styles.infoBox}>
              Use dry organic waste: maize stalks, prunings, wood scraps, etc.
              Biomass should be dry and organic.
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Biomass Type</label>
              <select
                style={styles.select}
                value={formData.biomass_type}
                onChange={e => setFormData({...formData, biomass_type: e.target.value})}
              >
                <option value="">-- Select Type --</option>
                {biomassTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Quantity</label>
                <input
                  type="number"
                  style={styles.input}
                  placeholder="e.g. 5"
                  value={formData.biomass_quantity}
                  onChange={e => setFormData({...formData, biomass_quantity: e.target.value})}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Unit</label>
                <select
                  style={styles.select}
                  value={formData.biomass_unit}
                  onChange={e => setFormData({...formData, biomass_unit: e.target.value})}
                >
                  <option value="buckets">Buckets</option>
                  <option value="wheelbarrows">Wheelbarrows</option>
                  <option value="kiln_loads">Kiln Loads</option>
                  <option value="kg">Kilograms</option>
                </select>
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Burn Date</label>
              <input
                type="date"
                style={styles.input}
                value={formData.burn_date}
                onChange={e => setFormData({...formData, burn_date: e.target.value})}
              />
            </div>

            <div style={styles.buttonRow}>
              <button style={styles.btnSecondary} onClick={onClose}>Save & Exit</button>
              <button 
                style={styles.btnPrimary} 
                onClick={handleStep1Submit}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Next Step'}
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Controlled Burn */}
        {currentStep === 2 && (
          <div style={styles.stepContent}>
            <h3 style={styles.stepTitle}>Step 2: Controlled Burn</h3>
            <div style={styles.infoBox}>
              <strong>Important:</strong> Stop at red embers, NOT ash!
              Extinguish safely with soil, sand, or water.
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Photo of Burn (Optional)</label>
              <div style={{ position: 'relative' }}>
                <div 
                  style={styles.photoUpload}
                  onClick={() => document.getElementById('burn-photo-input').click()}
                >
                  <div style={styles.uploadIcon}>📸</div>
                  <div style={{ color: '#888' }}>Take 1-2 photos of the burn</div>
                  <div style={{ color: '#666', fontSize: '11px' }}>Photos are auto-timestamped</div>
                  <input
                    id="burn-photo-input"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handlePhotoSelect(e, 'burn_photo')}
                  />
                </div>
                {formData.burn_photo_preview && (
                  <img src={formData.burn_photo_preview} alt="Burn preview" style={styles.photoPreview} />
                )}
                {formData.burn_photo && (
                  <div style={{ color: '#4ade80', fontSize: '12px', marginTop: '8px' }}>
                    ✓ Photo selected: {formData.burn_photo.name}
                  </div>
                )}
                {uploading && <div style={styles.uploadingOverlay}>Uploading...</div>}
              </div>
            </div>

            <div style={styles.safetyBox}>
              <div style={styles.safetyTitle}>⚠️ Safety Checklist:</div>
              <ul style={styles.safetyList}>
                <li>Fire stopped at red embers (not ash)</li>
                <li>Extinguished with soil/sand/water</li>
                <li>Left to cool for several hours</li>
              </ul>
            </div>

            <div style={styles.buttonRow}>
              <button style={styles.btnBack} onClick={() => setCurrentStep(1)}>Back</button>
              <button style={styles.btnSecondary} onClick={onClose}>Save & Exit</button>
              <button 
                style={styles.btnPrimary} 
                onClick={handleStep2Submit}
                disabled={loading || uploading}
              >
                {loading || uploading ? 'Saving...' : 'Next Step'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Activation */}
        {currentStep === 3 && (
          <div style={styles.stepContent}>
            <h3 style={styles.stepTitle}>Step 3: Activation</h3>
            <div style={styles.infoBox}>
              Charcoal must be activated to become biochar.
              Minimum 3 days activation time.
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Activation Method</label>
              <select
                style={styles.select}
                value={formData.activation_method}
                onChange={e => setFormData({...formData, activation_method: e.target.value})}
              >
                <option value="urine_water">Urine + Water (1:5-10 ratio)</option>
                <option value="compost">Compost Mix</option>
                <option value="manure_slurry">Manure Slurry</option>
                <option value="compost_tea">Compost Tea</option>
              </select>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Activation Start Date</label>
              <input
                type="date"
                style={styles.input}
                value={formData.activation_start_date}
                onChange={e => setFormData({...formData, activation_start_date: e.target.value})}
              />
            </div>

            <div style={styles.readyBox}>
              <div style={styles.readyTitle}>✓ Ready when:</div>
              <p style={styles.readyText}>Biochar is damp and smells like soil (3-7 days)</p>
            </div>

            <div style={styles.buttonRow}>
              <button style={styles.btnBack} onClick={() => setCurrentStep(2)}>Back</button>
              <button style={styles.btnSecondary} onClick={onClose}>Save & Exit</button>
              <button 
                style={styles.btnPrimary} 
                onClick={handleStep3Submit}
                disabled={loading}
              >
                {loading ? 'Saving...' : 'Next Step'}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Back to Soil */}
        {currentStep === 4 && (
          <div style={styles.stepContent}>
            <h3 style={styles.stepTitle}>Step 4: Back to Soil</h3>
            <div style={styles.infoBox}>
              Apply the activated biochar to your land.
              Mix into planting holes, beds, or fields.
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Application Date</label>
              <input
                type="date"
                style={styles.input}
                value={formData.application_date}
                onChange={e => setFormData({...formData, application_date: e.target.value})}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Application Area (Optional)</label>
              <input
                type="text"
                style={styles.input}
                placeholder="e.g. North field, garden beds"
                value={formData.application_area}
                onChange={e => setFormData({...formData, application_area: e.target.value})}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Photo of Application (Optional)</label>
              <div style={{ position: 'relative' }}>
                <div 
                  style={styles.photoUpload}
                  onClick={() => document.getElementById('application-photo-input').click()}
                >
                  <div style={styles.uploadIcon}>📷</div>
                  <div style={{ color: '#888' }}>Photo of biochar applied to soil</div>
                  <input
                    id="application-photo-input"
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handlePhotoSelect(e, 'application_photo')}
                  />
                </div>
                {formData.application_photo_preview && (
                  <img src={formData.application_photo_preview} alt="Application preview" style={styles.photoPreview} />
                )}
                {formData.application_photo && (
                  <div style={{ color: '#4ade80', fontSize: '12px', marginTop: '8px' }}>
                    ✓ Photo selected: {formData.application_photo.name}
                  </div>
                )}
                {uploading && <div style={styles.uploadingOverlay}>Uploading...</div>}
              </div>
            </div>

            <div style={styles.celebrationBox}>
              <div style={styles.celebrationTitle}>🎉 Almost Done!</div>
              <p style={styles.celebrationText}>Complete this step to earn your carbon credit</p>
            </div>

            <div style={styles.buttonRow}>
              <button style={styles.btnBack} onClick={() => setCurrentStep(3)}>Back</button>
              <button 
                style={{...styles.btnPrimary, background: '#22c55e'}} 
                onClick={handleStep4Submit}
                disabled={loading || uploading}
              >
                {loading || uploading ? 'Completing...' : '✓ Complete Burn'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}